/**
 * Everything a student's own account reads and writes.
 *
 * Mirrors what the iOS and Android clients call, endpoint for endpoint, so the three
 * platforms cannot drift into different behaviour for the same screen.
 */

import type { ApiClient } from '../core/client'
import type { ISODate, UUID } from '../types/common'
import type { Entitlement, Profile, ProfileUpdate, BugReport, BugReportCreate } from '../types/profile'
import type {
  Ack,
  Curriculum,
  CurriculumCreate,
  Subject,
  SubjectCreate,
  SubjectUpdate,
  SyncResult,
  Topic,
  TopicCreate,
  TopicUpdate,
  Unit,
  UnitCreate,
  UnitUpdate,
} from '../types/curriculum'
import type {
  ActivityUpdate,
  DailyMetrics,
  DailyMetricsUpsert,
  GenerateScheduleRequest,
  HomeRecommendation,
  MetricsSummary,
  RescheduleRequest,
  ScheduledActivity,
  SessionState,
  SkipRequest,
  StudentTask,
  StudentTaskCreate,
  StudyPreferences,
  TimetableDocument,
  UpcomingExam,
  UpcomingExamCreate,
} from '../types/planner'
import type { LeaderboardConsent, LeaderboardResponse } from '../types/leaderboard'
import type { CheckoutSession, Plan, Subscription } from '../types/school'

export class LearnerResource {
  constructor(private readonly client: ApiClient) {}

  // -- profile ---------------------------------------------------------------

  profile(signal?: AbortSignal): Promise<Profile> {
    return this.client.get<Profile>('/users/profile/', undefined, signal)
  }

  updateProfile(update: ProfileUpdate): Promise<Profile> {
    return this.client.put<Profile>('/users/profile/', update)
  }

  /** Irreversible, and it is the student's right. The caller confirms; this does not. */
  deleteAccount(): Promise<void> {
    return this.client.delete<void>('/users/profile/')
  }

  /** What the caller may do right now. `source: 'school'` means a limit they hit is
   *  never a paywall — a school student is already paid for. */
  entitlements(signal?: AbortSignal): Promise<Entitlement> {
    return this.client.get<Entitlement>('/entitlements', undefined, signal)
  }

  reportBug(report: BugReportCreate): Promise<BugReport> {
    return this.client.post<BugReport>('/support/report-bug', report)
  }

  // -- curriculum ------------------------------------------------------------

  curriculums(signal?: AbortSignal): Promise<Curriculum[]> {
    return this.client.get<Curriculum[]>('/curriculums', undefined, signal)
  }

  createCurriculum(body: CurriculumCreate): Promise<Curriculum> {
    return this.client.post<Curriculum>('/curriculums', body)
  }

  /** Enrols the student. Returns only an acknowledgement — refetch `curriculums()`
   *  for the resulting state. */
  selectCurriculum(curriculumId: UUID): Promise<Ack> {
    return this.client.put<Ack>(`/curriculums/${curriculumId}/select`)
  }

  addSubject(curriculumId: UUID, body: SubjectCreate): Promise<Subject> {
    return this.client.post<Subject>(`/curriculums/${curriculumId}/subjects`, body)
  }

  syncTemplates(): Promise<SyncResult> {
    return this.client.post<SyncResult>('/curriculums/sync-templates')
  }

  updateSubject(subjectId: UUID, body: SubjectUpdate): Promise<Subject> {
    return this.client.put<Subject>(`/subjects/${subjectId}`, body)
  }

  deleteSubject(subjectId: UUID): Promise<void> {
    return this.client.delete<void>(`/subjects/${subjectId}`)
  }

  /**
   * Adds or removes a subject from the student's own selection. Does not delete it.
   *
   * Uses `/selection` rather than the older `/toggle-selection`. Two reasons, and
   * the first is that the old route was simply broken from here: it declares a
   * required `body: dict`, so calling it without one is a 422 — which is what the
   * onboarding "Start with N subjects" button was hitting. The second is that
   * despite the name it never toggled; it read `is_selected` from that body and
   * defaulted to `true`, so it could select and never deselect.
   *
   * Sending the state we want also means two devices racing cannot flip each other
   * back and forth, which is why the catalogue-era route exists.
   */
  setSubjectSelection(subjectId: UUID, isSelected: boolean): Promise<Ack> {
    return this.client.put<Ack>(`/subjects/${subjectId}/selection`, {
      is_selected: isSelected,
    })
  }

  units(subjectId: UUID, signal?: AbortSignal): Promise<Unit[]> {
    return this.client.get<Unit[]>(`/subjects/${subjectId}/units`, undefined, signal)
  }

  addUnit(subjectId: UUID, body: UnitCreate): Promise<Unit> {
    return this.client.post<Unit>(`/subjects/${subjectId}/units`, body)
  }

  updateUnit(unitId: UUID, body: UnitUpdate): Promise<Unit> {
    return this.client.put<Unit>(`/units/${unitId}`, body)
  }

  deleteUnit(unitId: UUID): Promise<void> {
    return this.client.delete<void>(`/units/${unitId}`)
  }

  topics(unitId: UUID, signal?: AbortSignal): Promise<Topic[]> {
    return this.client.get<Topic[]>(`/units/${unitId}/topics`, undefined, signal)
  }

  addTopic(unitId: UUID, body: TopicCreate): Promise<Topic> {
    return this.client.post<Topic>(`/units/${unitId}/topics`, body)
  }

  updateTopic(topicId: UUID, body: TopicUpdate): Promise<Topic> {
    return this.client.put<Topic>(`/topics/${topicId}`, body)
  }

  deleteTopic(topicId: UUID): Promise<void> {
    return this.client.delete<void>(`/topics/${topicId}`)
  }

  // -- home and metrics ------------------------------------------------------

  recommendations(signal?: AbortSignal): Promise<HomeRecommendation[]> {
    return this.client.get<HomeRecommendation[]>('/home/recommendations', undefined, signal)
  }

  /**
   * Note the path: `/progress`, not `/metrics`.
   *
   * Content blockers match `/metrics` — it is what analytics endpoints are
   * called — so for a student running uBlock the request is killed in the browser
   * and Home never loads, with nothing in any server log to explain it. The BFF
   * maps it back before it reaches the API, which keeps its own route names.
   */
  metricsSummary(signal?: AbortSignal): Promise<MetricsSummary> {
    return this.client.get<MetricsSummary>('/progress/summary', undefined, signal)
  }

  dailyMetrics(days?: number, signal?: AbortSignal): Promise<DailyMetrics[]> {
    return this.client.get<DailyMetrics[]>('/progress/daily', { days }, signal)
  }

  upsertDailyMetrics(body: DailyMetricsUpsert): Promise<DailyMetrics> {
    return this.client.post<DailyMetrics>('/progress/daily', body)
  }

  // -- tasks and exams -------------------------------------------------------

  tasks(signal?: AbortSignal): Promise<StudentTask[]> {
    return this.client.get<StudentTask[]>('/tasks/', undefined, signal)
  }

  createTask(body: StudentTaskCreate): Promise<StudentTask> {
    return this.client.post<StudentTask>('/tasks/', body)
  }

  updateTask(taskId: UUID, body: StudentTaskCreate): Promise<StudentTask> {
    return this.client.put<StudentTask>(`/tasks/${taskId}`, body)
  }

  deleteTask(taskId: UUID): Promise<void> {
    return this.client.delete<void>(`/tasks/${taskId}`)
  }

  exams(signal?: AbortSignal): Promise<UpcomingExam[]> {
    return this.client.get<UpcomingExam[]>('/exams/', undefined, signal)
  }

  createExam(body: UpcomingExamCreate): Promise<UpcomingExam> {
    return this.client.post<UpcomingExam>('/exams/', body)
  }

  updateExam(examId: UUID, body: UpcomingExamCreate): Promise<UpcomingExam> {
    return this.client.put<UpcomingExam>(`/exams/${examId}`, body)
  }

  deleteExam(examId: UUID): Promise<void> {
    return this.client.delete<void>(`/exams/${examId}`)
  }

  // -- timetable -------------------------------------------------------------

  timetable(signal?: AbortSignal): Promise<TimetableDocument> {
    return this.client.get<TimetableDocument>('/timetable', undefined, signal)
  }

  /** The whole document, every time. Idempotent by design, which is what lets a
   *  queued offline write be replayed without reconciling per-cell diffs. */
  saveTimetable(document: TimetableDocument): Promise<TimetableDocument> {
    return this.client.put<TimetableDocument>('/timetable', document)
  }

  // -- smart schedule --------------------------------------------------------

  activities(
    params?: { from?: ISODate; to?: ISODate },
    signal?: AbortSignal,
  ): Promise<ScheduledActivity[]> {
    return this.client.get<ScheduledActivity[]>('/activities', params, signal)
  }

  activity(activityId: UUID, signal?: AbortSignal): Promise<ScheduledActivity> {
    return this.client.get<ScheduledActivity>(`/activities/${activityId}`, undefined, signal)
  }

  updateActivityState(activityId: UUID, body: ActivityUpdate): Promise<ScheduledActivity> {
    return this.client.patch<ScheduledActivity>(`/activities/${activityId}/state`, body)
  }

  skipActivity(activityId: UUID, body: SkipRequest = {}): Promise<ScheduledActivity> {
    return this.client.post<ScheduledActivity>(`/activities/${activityId}/skip`, body)
  }

  rescheduleActivity(activityId: UUID, body: RescheduleRequest): Promise<ScheduledActivity> {
    return this.client.post<ScheduledActivity>(`/activities/${activityId}/reschedule`, body)
  }

  deleteActivity(activityId: UUID): Promise<void> {
    return this.client.delete<void>(`/activities/${activityId}`)
  }

  /** The in-flight session, if any — what lets a focus session started on a phone
   *  be picked up on the web mid-question. */
  activeSessionState(signal?: AbortSignal): Promise<SessionState | null> {
    return this.client.get<SessionState | null>('/activities/session-state/active', undefined, signal)
  }

  saveSessionState(activityId: UUID, state: SessionState): Promise<SessionState> {
    return this.client.put<SessionState>(`/activities/${activityId}/session-state`, state)
  }

  clearSessionState(activityId: UUID): Promise<void> {
    return this.client.delete<void>(`/activities/${activityId}/session-state`)
  }

  generateSchedule(body: GenerateScheduleRequest = {}): Promise<ScheduledActivity[]> {
    return this.client.post<ScheduledActivity[]>('/schedule/generate', body)
  }

  studyPreferences(signal?: AbortSignal): Promise<StudyPreferences> {
    return this.client.get<StudyPreferences>('/schedule/preferences', undefined, signal)
  }

  saveStudyPreferences(body: Partial<StudyPreferences>): Promise<StudyPreferences> {
    return this.client.put<StudyPreferences>('/schedule/preferences', body)
  }

  // -- leaderboard -----------------------------------------------------------

  /** `tier` is one of class | year | school. An unavailable board carries a reason
   *  rather than an empty list with no explanation. */
  leaderboard(
    params?: { tier?: 'class' | 'year' | 'school'; subjectId?: UUID; limit?: number },
    signal?: AbortSignal,
  ): Promise<LeaderboardResponse> {
    return this.client.get<LeaderboardResponse>('/leaderboard', params, signal)
  }

  leaderboardConsent(signal?: AbortSignal): Promise<LeaderboardConsent> {
    return this.client.get<LeaderboardConsent>('/leaderboard/consent', undefined, signal)
  }

  setLeaderboardConsent(optIn: boolean): Promise<LeaderboardConsent> {
    return this.client.put<LeaderboardConsent>('/leaderboard/consent', { optIn })
  }

  // -- billing ---------------------------------------------------------------

  plans(signal?: AbortSignal): Promise<Plan[]> {
    return this.client.get<Plan[]>('/billing/plans', undefined, signal)
  }

  subscription(signal?: AbortSignal): Promise<Subscription> {
    return this.client.get<Subscription>('/billing/subscription', undefined, signal)
  }

  /** Returns a Stripe Checkout URL to redirect to. Web sells directly, which is why
   *  there is no in-app-purchase path here. */
  checkoutSession(planCode: string): Promise<CheckoutSession> {
    return this.client.post<CheckoutSession>('/billing/checkout-session', { planCode })
  }

  /** The Stripe Customer Portal — where a subscription is changed or cancelled. */
  portalSession(): Promise<CheckoutSession> {
    return this.client.post<CheckoutSession>('/billing/portal-session')
  }
}
